/**
 * EmbedCollectionPage — встраиваемая версия страницы курса
 * apps/web/src/pages/embed/EmbedCollectionPage.tsx
 * 
 * Отличия от CollectionDetailPage:
 * - Нет кнопки "Назад к коллекциям" (некуда возвращаться)
 * - EmbedPaywall вместо PurchaseButton (Stars)
 * - Навигация внутри /embed/collection/:id/...
 */

import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCollectionsStore } from '../../stores/collections';
import { EmbedPaywall } from '../../components/EmbedLayout';
import type { Section, Module, UserCollectionProgress } from '@beshek/shared';

// Переиспользуем логику статусов из основной версии
type ModuleStatus = 'locked' | 'available' | 'started' | 'completed';

function getModuleStatus(
  module: Module,
  moduleIndex: number,
  section: Section,
  sectionIndex: number,
  progress: UserCollectionProgress | null,
  hasAccess: boolean,
  previewSections: number,
  allSections: Section[],
  sequential: boolean
): ModuleStatus {
  if (!hasAccess && sectionIndex >= previewSections) return 'locked';

  const moduleProgress = progress?.moduleProgress[module.id];
  if (moduleProgress?.completed) return 'completed';
  if (moduleProgress?.started) return 'started';
  if (sectionIndex === 0 && moduleIndex === 0) return 'available';
  // Курс без строгого порядка открыт весь — как и в основном приложении
  if (!sequential) return 'available';

  if (sequential && moduleIndex === 0 && sectionIndex > 0) {
    const prevSection = allSections[sectionIndex - 1];
    const allPrevCompleted = prevSection.modules.every((m: Module) =>
      progress?.moduleProgress[m.id]?.completed
    );
    return allPrevCompleted ? 'available' : 'locked';
  }

  const prevModule = section.modules[moduleIndex - 1];
  const prevProgress = progress?.moduleProgress[prevModule.id];
  return prevProgress?.completed ? 'available' : 'locked';
}

export default function EmbedCollectionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    currentCollection,
    currentProgress,
    hasAccess,
    isLoading,
    error,
    loadCollectionDetail,
    startModule,
  } = useCollectionsStore();

  useEffect(() => {
    if (id) loadCollectionDetail(id);
  }, [id]);

  const handleModuleStart = async (moduleId: string) => {
    if (!id) return;
    await startModule(id, moduleId);
    // Навигация внутри embed
    navigate(`/embed/collection/${id}/module/${moduleId}`);
  };

  if (isLoading || !currentCollection) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-32 bg-gray-200 rounded-2xl" />
          <div className="h-48 bg-gray-200 rounded-2xl" />
        </div>
      </div>
    );
  }

  const collection = currentCollection;
  const previewSections = collection.access.previewSections ?? 1;
  const completionPercent = currentProgress
    ? Math.round(
        (Object.values(currentProgress.moduleProgress).filter((m: any) => m.completed).length /
          (collection.stats.totalModules || 1)) *
          100
      )
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header — упрощённый, без кнопки Назад */}
      <div className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="flex items-start gap-4">
          <div className="text-5xl">{collection.icon}</div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{collection.name}</h1>
            <p className="text-sm text-gray-500 mt-1">{collection.description}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Progress */}
        {currentProgress && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Прогресс</span>
              <span className="text-sm font-medium text-blue-600">{completionPercent}%</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
            <div className="text-sm text-gray-500">⚡ +{currentProgress.xpEarned} XP</div>
          </div>
        )}

        {/* Sections */}
        {collection.sections.map((section: Section, sectionIndex: number) => {
          const isLocked = !hasAccess && sectionIndex >= previewSections;

          return (
            <div
              key={section.id}
              className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden
                         ${isLocked ? 'opacity-75' : ''}`}
            >
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">{section.name}</h3>
                {section.description && (
                  <p className="text-sm text-gray-500 mt-0.5">{section.description}</p>
                )}
                {isLocked && (
                  <p className="text-sm text-amber-600 mt-1">🔒 Доступно в полной версии курса</p>
                )}
              </div>

              <div className="p-3 space-y-2">
                {section.modules.map((module: Module, moduleIndex: number) => {
                  const status = getModuleStatus(
                    module, moduleIndex, section, sectionIndex,
                    currentProgress, hasAccess, previewSections,
                    collection.sections, collection.sequential !== false
                  );
                  const mp = currentProgress?.moduleProgress[module.id];

                  const statusConfig: Record<string, any> = {
                    locked: { icon: '🔒', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-400' },
                    available: { icon: '▶️', bg: 'bg-white', border: 'border-gray-200 hover:border-blue-300', text: 'text-gray-900' },
                    started: { icon: '⏳', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900' },
                    completed: { icon: '✅', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900' },
                  };
                  const config = statusConfig[status];
                  const isClickable = status !== 'locked';

                  return (
                    <button
                      key={module.id}
                      onClick={isClickable ? () => handleModuleStart(module.id) : undefined}
                      disabled={!isClickable}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all
                                  ${config.bg} ${config.border} ${!isClickable ? 'cursor-not-allowed' : ''}`}
                    >
                      <span className="text-2xl">{config.icon}</span>
                      <div className="flex-1 text-left">
                        <h4 className={`font-medium ${config.text}`}>{module.name}</h4>
                        {module.description && (
                          <p className="text-sm text-gray-500 mt-0.5">{module.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {mp?.bestScore !== undefined && status === 'completed' && (
                          <span className="text-sm font-medium text-green-600">{mp.bestScore}%</span>
                        )}
                        {isClickable && <span className="text-blue-500">→</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Paywall — вместо Stars-покупки */}
        {!hasAccess && (
          <EmbedPaywall 
            collectionId={id || ''} 
            collectionName={collection.name} 
          />
        )}
      </div>
    </div>
  );
}